import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPropertyVersions, revertPropertyVersion } from '../../Redux/Actions/actions';
import style from './VersionHistory.module.css';

const FIELD_LABELS = {
  price: 'Precio', title: 'Título', description: 'Descripción',
  currency: 'Moneda', expenses: 'Expensas', street: 'Calle',
  number: 'Número', province: 'Provincia', locality: 'Localidad',
  neighborhood: 'Barrio', rooms: 'Habitaciones', bathrooms: 'Baños',
  environments: 'Ambientes', garages: 'Cocheras',
  totalSquareMeters: 'M² Totales', coveredSquareMeters: 'M² Cubiertos',
  semiCoveredSquareMeters: 'M² Semicubiertos', uncovered: 'M² Descubiertos',
  propertyType: 'Tipo de Propiedad', propertyState: 'Estado',
  isForSale: 'En Venta', isForRent: 'En Alquiler',
  isFinished: 'Terminado', isUnderDevelopment: 'En Construcción',
  age: 'Antigüedad', land: 'Terreno (m²)',
  ownerName: 'Propietario', ownerPhone: 'Teléfono', ownerEmail: 'Email',
  photo: 'Fotos', documentation: 'Documentación',
  services: 'Servicios', amenities: 'Amenities',
  characteristics: 'Características', detailsProperty: 'Detalles',
  environmentsOptions: 'Ambientes/Espacios',
};

// Labels legibles para los sub-campos de cada JSON field
const SERVICE_LABELS = {
  electricidad: 'Electricidad', agua: 'Agua', gas: 'Gas', internet: 'Internet',
  telefono: 'Teléfono', desagueCloacal: 'Desagüe Cloacal',
  televisionPorCable: 'TV por Cable', alarma: 'Alarma',
  televisionSatelital: 'TV Satelital', aguaCorriente: 'Agua Corriente',
};

const AMENITY_LABELS = {
  aireAcondicionado: 'Aire Acondicionado', portonAutomatico: 'Portón Automático',
  gimnasio: 'Gimnasio', losaRadiante: 'Losa Radiante', chimenea: 'Chimenea',
  hidromasaje: 'Hidromasaje', seguridad: 'Seguridad', pileta: 'Pileta',
  caldera: 'Caldera', businessCenter: 'Business Center', areaCine: 'Área de Cine',
  cisterna: 'Cisterna', laundry: 'Laundry',
  estacionamientoVisitas: 'Estac. Visitas', ascensor: 'Ascensor',
  salonUsosMultiples: 'SUM', areaDeJuegosInfantiles: 'Juegos Infantiles',
  canchaTenis: 'Cancha de Tenis', recepcion: 'Recepción',
  areasVerdes: 'Áreas Verdes', valetParking: 'Valet Parking',
  canchaBasquetbol: 'Cancha Básquet', canchaFutbol: 'Cancha Fútbol',
  canchaPaddle: 'Cancha Paddle', solarium: 'Solarium',
  jardinDeInvierno: 'Jardín de Invierno', piletaCubierta: 'Pileta Cubierta',
  piletaClimatizada: 'Pileta Climatizada', sauna: 'Sauna', bar: 'Bar',
  calefaccion: 'Calefacción',
};

const CHARACTERISTIC_LABELS = {
  placard: 'Placard', parilla: 'Parrilla', desayunador: 'Desayunador',
  orientacionSur: 'Orient. Sur', orientacionOeste: 'Orient. Oeste',
  orientacionNorte: 'Orient. Norte', orientacionEste: 'Orient. Este',
  orientacionNoroeste: 'Orient. Noroeste', orientacionNoreste: 'Orient. Noreste',
  orientacionSuroeste: 'Orient. Suroeste', orientacionSureste: 'Orient. Sureste',
  accesoDeCocheraRampaFija: 'Cochera Rampa Fija',
  accesoDeCocheraRampaMovil: 'Cochera Rampa Móvil',
  accesoDeCocheraAscensor: 'Cochera Ascensor',
  accesoDeCocheraHorizontal: 'Cochera Horizontal',
  disposicionContrafrente: 'Contrafrente', disposicionFrente: 'Frente',
  disposicionInterno: 'Interno', disposicionLateral: 'Lateral',
  amoblado: 'Amoblado', deck: 'Deck', bodegas: 'Bodegas',
  tipoDeBodegaComercial: 'Bodega Comercial',
  tipoDeBodegaNaveIndustrial: 'Bodega Nave Industrial',
  tipoDeBodegaAlmacen: 'Bodega Almacén',
  tipoDeCampoOtro: 'Campo Otro', tipoDeCampoFruticula: 'Campo Frutícola',
  tipoDeCampoAgricola: 'Campo Agrícola', tipoDeCampoChara: 'Campo Chacra',
  tipoDeCampoCriadero: 'Campo Criadero', tipoDeCampoTambero: 'Campo Tambero',
  tipoDeCampoFloricultura: 'Campo Floricultura',
  tipoDeCampoForestal: 'Campo Forestal', tipoDeCampoGanadero: 'Campo Ganadero',
  tipoDeCampoHaras: 'Campo Haras', biblioteca: 'Biblioteca',
  galpon: 'Galpón', sotano: 'Sótano', baulera: 'Baulera',
  permiteMascota: 'Permite Mascota', aptoTuristico: 'Apto Turístico',
};

const DETAIL_LABELS = {
  exclusiveContract: 'Contrato Exclusivo', cartel: 'Cartel',
  financing: 'Financiamiento', suitableCredit: 'Apto Crédito',
  commercialSuitable: 'Apto Comercial', professionalSuitable: 'Apto Profesional',
  suitableForReducedMobility: 'Movilidad Reducida', pozo: 'Pozo',
  CountryOrPrivateNeighborhood: 'Country/Barrio Privado',
};

const ENVIRONMENT_LABELS = {
  dormitorio: 'Dormitorio', comedor: 'Comedor', vestidor: 'Vestidor',
  jardin: 'Jardín', baño: 'Baño', patio: 'Patio', terraza: 'Terraza',
  estudio: 'Estudio', lavadero: 'Lavadero', altillo: 'Altillo',
  playroom: 'Playroom', lobby: 'Lobby', quincho: 'Quincho',
  salaDeReuniones: 'Sala de Reuniones', balcon: 'Balcón', pileta: 'Pileta',
  cocina: 'Cocina', toilette: 'Toilette', habitacion: 'Habitación',
  living: 'Living', otro: 'Otro',
};

// Mapa de campo → diccionario de sub-labels
const JSON_FIELD_LABELS = {
  services: SERVICE_LABELS,
  amenities: AMENITY_LABELS,
  characteristics: CHARACTERISTIC_LABELS,
  detailsProperty: DETAIL_LABELS,
  environmentsOptions: ENVIRONMENT_LABELS,
};

const isJsonToggleField = (campo) => campo in JSON_FIELD_LABELS;

// Parsea un valor que puede ser objeto o string JSON
const parseJsonValue = (val) => {
  if (!val) return {};
  if (typeof val === 'object' && !Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try { return JSON.parse(val); } catch { return {}; }
  }
  return {};
};

// Obtiene las keys activas ("true") de un objeto toggle
const getActiveKeys = (obj) => {
  return Object.entries(obj)
    .filter(([, v]) => String(v).toLowerCase() === 'true' || v === true)
    .map(([k]) => k);
};

const SKIP_FIELDS = ['id', 'sellerId', 'createdAt', 'updatedAt', 'statusProperty', 'photo', 'documentation'];

// Normaliza un valor a string canónico para comparación robusta entre tipos
const normalizeValue = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).trim().toLowerCase();
  // Booleanos y sus equivalentes string
  if (v === true || s === 'true' || s === 'sí' || s === 'si') return 'true';
  if (v === false || s === 'false' || s === 'no') return 'false';
  // Números y sus equivalentes string
  if (typeof v === 'number') return v.toString();
  if (s !== '' && !isNaN(Number(s))) return Number(s).toString();
  // Arrays/objetos
  if (typeof v === 'object') return JSON.stringify(v);
  return s;
};

const valuesAreEqual = (a, b) => normalizeValue(a) === normalizeValue(b);

const VersionHistory = ({ propertyId }) => {
  const dispatch = useDispatch();
  const propertyVersions = useSelector((state) => state.propertyVersions);
  const versioningLoading = useSelector((state) => state.versioningLoading);
  const userId = useSelector((state) => state.userId);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (propertyId) {
      dispatch(fetchPropertyVersions(propertyId));
    }
  }, [dispatch, propertyId]);

  // Compara una versión antigua contra la versión actual (index 0)
  // Muestra TODAS las diferencias acumuladas entre esa versión y el estado actual
  const getDiffsWithCurrent = (olderVersion, olderIdx) => {
    if (!propertyVersions || propertyVersions.length < 2 || !olderVersion) return [];
    const currentVersion = propertyVersions[0];
    const currentData = currentVersion.datos || {};
    const olderData = olderVersion.datos || {};

    // Opción 1: Comparar objetos datos completos si ambos existen
    if (Object.keys(currentData).length > 0 && Object.keys(olderData).length > 0) {
      const allKeys = new Set([...Object.keys(currentData), ...Object.keys(olderData)]);
      const diffs = [];
      allKeys.forEach((key) => {
        if (SKIP_FIELDS.includes(key)) return;
        const oldVal = olderData[key];
        const curVal = currentData[key];
        if (!valuesAreEqual(oldVal, curVal)) {
          diffs.push({ campo: key, valorEnEstaVersion: oldVal, valorActual: curVal });
        }
      });
      return diffs;
    }

    // Opción 2: Reconstruir diffs acumulando los "cambios" de las versiones intermedias
    // Recorremos desde la versión en idx=1 hasta olderIdx, acumulando todos los campos que cambiaron
    const accumulated = {};
    for (let i = 1; i <= olderIdx; i++) {
      const v = propertyVersions[i];
      if (v.cambios && typeof v.cambios === 'object') {
        Object.entries(v.cambios).forEach(([campo, { anterior, nuevo }]) => {
          if (SKIP_FIELDS.includes(campo)) return;
          if (!accumulated[campo]) {
            // Primera vez que vemos este campo: "nuevo" de esta versión es el valor más cercano al actual
            accumulated[campo] = { valorActual: nuevo, valorEnEstaVersion: anterior };
          } else {
            // Ya lo habíamos visto en una versión más reciente: solo actualizamos el valor viejo
            accumulated[campo].valorEnEstaVersion = anterior;
          }
        });
      }
    }

    // Filtrar solo los que realmente difieren
    return Object.entries(accumulated)
      .filter(([, { valorActual, valorEnEstaVersion }]) => !valuesAreEqual(valorActual, valorEnEstaVersion))
      .map(([campo, { valorActual, valorEnEstaVersion }]) => ({
        campo,
        valorEnEstaVersion,
        valorActual,
      }));
  };

  const handleRevert = async (versionId) => {
    const confirmed = window.confirm('¿Revertir la propiedad a esta versión? Se creará una nueva versión de tipo "Reversión".');
    if (!confirmed) return;
    try {
      await dispatch(revertPropertyVersion(versionId, userId));
      alert('Propiedad revertida correctamente.');
      dispatch(fetchPropertyVersions(propertyId));
    } catch {
      alert('Error al revertir la versión.');
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('es-AR', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatRelative = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    return null; // use absolute date
  };

  const renderValue = (val, campo) => {
    if (val === null || val === undefined) return <span className={style.nullValue}>vacío</span>;
    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
    if (Array.isArray(val)) return `${val.length} elemento${val.length !== 1 ? 's' : ''}`;
    // Campos JSON toggle: mostrar solo los activos como chips
    if (typeof val === 'object' && campo && isJsonToggleField(campo)) {
      const labelMap = JSON_FIELD_LABELS[campo];
      const activeKeys = getActiveKeys(val);
      if (activeKeys.length === 0) return <span className={style.nullValue}>ninguno</span>;
      return (
        <span className={style.jsonChipsInline}>
          {activeKeys.map((k) => (
            <span key={k} className={style.jsonChip}>{labelMap[k] || k}</span>
          ))}
        </span>
      );
    }
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  // Renderiza la comparación de un campo JSON toggle como chips con diff visual
  const renderJsonDiff = (campo, oldVal, newVal) => {
    const labelMap = JSON_FIELD_LABELS[campo];
    const oldObj = parseJsonValue(oldVal);
    const newObj = parseJsonValue(newVal);
    const oldActive = new Set(getActiveKeys(oldObj));
    const newActive = new Set(getActiveKeys(newObj));

    const allKeys = new Set([...oldActive, ...newActive]);
    const added = [];
    const removed = [];
    const unchanged = [];

    allKeys.forEach((k) => {
      const label = labelMap[k] || k;
      if (newActive.has(k) && !oldActive.has(k)) added.push({ key: k, label });
      else if (oldActive.has(k) && !newActive.has(k)) removed.push({ key: k, label });
      else unchanged.push({ key: k, label });
    });

    return (
      <div className={style.jsonDiffContent}>
        {added.map(({ key, label }) => (
          <span key={key} className={`${style.jsonChip} ${style.chipAdded}`}>+ {label}</span>
        ))}
        {removed.map(({ key, label }) => (
          <span key={key} className={`${style.jsonChip} ${style.chipRemoved}`}>− {label}</span>
        ))}
        {unchanged.map(({ key, label }) => (
          <span key={key} className={`${style.jsonChip} ${style.chipUnchanged}`}>{label}</span>
        ))}
        {allKeys.size === 0 && <span className={style.nullValue}>ninguno</span>}
      </div>
    );
  };

  // Cuenta cambios internos de un campo JSON toggle
  const getJsonChangeCounts = (oldVal, newVal) => {
    const oldObj = parseJsonValue(oldVal);
    const newObj = parseJsonValue(newVal);
    const oldActive = new Set(getActiveKeys(oldObj));
    const newActive = new Set(getActiveKeys(newObj));
    let added = 0, removed = 0;
    newActive.forEach((k) => { if (!oldActive.has(k)) added++; });
    oldActive.forEach((k) => { if (!newActive.has(k)) removed++; });
    return { added, removed };
  };

  const getChangeSummary = (version) => {
    if (!version.cambios) return null;
    const fields = Object.entries(version.cambios)
      .filter(([f]) => !SKIP_FIELDS.includes(f))
      .filter(([, { anterior, nuevo }]) => !valuesAreEqual(anterior, nuevo))
      .map(([f, { anterior, nuevo }]) => ({ field: f, anterior, nuevo }));
    if (fields.length === 0) return null;
    const labels = fields.slice(0, 3).map(({ field, anterior, nuevo }) => {
      const label = FIELD_LABELS[field] || field;
      if (isJsonToggleField(field)) {
        const { added, removed } = getJsonChangeCounts(anterior, nuevo);
        const parts = [];
        if (added > 0) parts.push(`+${added}`);
        if (removed > 0) parts.push(`−${removed}`);
        return parts.length > 0 ? `${label} (${parts.join(', ')})` : label;
      }
      return label;
    });
    const extra = fields.length > 3 ? ` y ${fields.length - 3} más` : '';
    return labels.join(', ') + extra;
  };

  if (versioningLoading && propertyVersions.length === 0) {
    return <div className={style.loading}>Cargando versiones...</div>;
  }

  if (!propertyVersions || propertyVersions.length === 0) {
    return <div className={style.empty}>No hay versiones registradas para esta propiedad.</div>;
  }

  const isCurrentVersion = (idx) => idx === 0;

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h3 className={style.title}>Historial de Versiones</h3>
        <span className={style.versionCount}>{propertyVersions.length} versión{propertyVersions.length !== 1 ? 'es' : ''}</span>
      </div>

      <div className={style.timeline}>
        {propertyVersions.map((version, idx) => {
          const isCurrent = isCurrentVersion(idx);
          const diffs = isCurrent ? [] : getDiffsWithCurrent(version, idx);
          const changeSummary = getChangeSummary(version);
          const relative = formatRelative(version.createdAt);

          return (
            <div key={version.id} className={`${style.versionCard} ${isCurrent ? style.currentCard : ''}`}>
              {/* Línea de timeline */}
              <div className={style.timelineDot}>
                <div className={`${style.dot} ${isCurrent ? style.dotCurrent : ''}`} />
                {idx < propertyVersions.length - 1 && <div className={style.line} />}
              </div>

              <div className={style.versionBody}>
                <div
                  className={style.versionHeader}
                  onClick={() => !isCurrent && toggleExpand(version.id)}
                  role={isCurrent ? undefined : 'button'}
                  tabIndex={isCurrent ? undefined : 0}
                >
                  <div className={style.versionTop}>
                    <span className={`${style.versionBadge} ${isCurrent ? style.badgeCurrent : ''}`}>
                      {isCurrent ? 'Actual' : `v${version.version}`}
                    </span>
                    <div className={style.dateGroup}>
                      <span className={style.versionDate}>{formatDate(version.createdAt)}</span>
                      <span className={style.versionTime}>{formatTime(version.createdAt)}</span>
                      {relative && <span className={style.relativeDate}>{relative}</span>}
                    </div>
                  </div>

                  <div className={style.versionMeta}>
                    {version.editor && (
                      <span className={style.versionEditor}>
                        {version.editor.photo ? (
                          <img src={version.editor.photo} alt="" className={style.editorAvatar} />
                        ) : (
                          <span className={style.editorInitial}>
                            {(version.editor.name || '?')[0]}
                          </span>
                        )}
                        {version.editor.name} {version.editor.last_name}
                      </span>
                    )}
                    {!isCurrent && changeSummary && (
                      <span className={style.changeSummary}>Cambió: {changeSummary}</span>
                    )}
                    {!isCurrent && (
                      <span className={style.expandIcon}>{expandedId === version.id ? '▲' : '▼'}</span>
                    )}
                  </div>

                  {version.motivo && (
                    <p className={style.motivo}>
                      <span className={style.motivoLabel}>Motivo:</span> {version.motivo}
                    </p>
                  )}
                </div>

                {/* Diff expandible — Diferencias de esta versión vs la actual */}
                {!isCurrent && expandedId === version.id && (
                  <div className={style.diffContainer}>
                    {diffs.length > 0 ? (
                      <>
                        <div className={style.diffHeader}>
                          <span className={style.diffHeaderLabel}>
                            {diffs.length} diferencia{diffs.length !== 1 ? 's' : ''} con la versión actual
                          </span>
                        </div>
                        <div className={style.diffTable}>
                          <div className={style.diffTableHeader}>
                            <span>Campo</span>
                            <span>En esta versión</span>
                            <span>Actual</span>
                          </div>
                          {diffs.map(({ campo, valorEnEstaVersion, valorActual }) => (
                            isJsonToggleField(campo) ? (
                              <div key={campo} className={style.jsonDiffRow}>
                                <span className={style.diffField}>{FIELD_LABELS[campo] || campo}</span>
                                {renderJsonDiff(campo, valorEnEstaVersion, valorActual)}
                              </div>
                            ) : (
                              <div key={campo} className={style.diffRow}>
                                <span className={style.diffField}>{FIELD_LABELS[campo] || campo}</span>
                                <span className={style.diffOld}>{renderValue(valorEnEstaVersion, campo)}</span>
                                <span className={style.diffNew}>{renderValue(valorActual, campo)}</span>
                              </div>
                            )
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className={style.noDiffs}>Idéntica a la versión actual.</p>
                    )}
                    <button
                      className={style.revertButton}
                      onClick={(e) => { e.stopPropagation(); handleRevert(version.id); }}
                      disabled={versioningLoading}
                    >
                      ↩ Revertir a esta versión
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VersionHistory;
